import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../api.js';
import { TOKEN_STORAGE_KEY } from '../constants.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_STORAGE_KEY));
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadUser = useCallback(async (nextToken = token) => {
    if (!nextToken) {
      return null;
    }

    const payload = await apiFetch('/api/auth/me', { token: nextToken });
    return payload.user || null;
  }, [token]);

  const refreshUser = useCallback(async (nextToken = token) => {
    const nextUser = await loadUser(nextToken);
    setUser(nextUser);
    return nextUser;
  }, [loadUser, token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    setToken(null);
    setUser(null);
  };

  const login = async ({ email, password }) => {
    const payload = await apiFetch('/api/auth/login', { method: 'POST', body: { email, password } });
    localStorage.setItem(TOKEN_STORAGE_KEY, payload.token);
    setToken(payload.token);
    setUser(payload.user);
    return payload;
  };

  const register = async ({ email, password, name }) => {
    const payload = await apiFetch('/api/auth/register', { method: 'POST', body: { email, password, name } });
    localStorage.setItem(TOKEN_STORAGE_KEY, payload.token);
    setToken(payload.token);
    setUser(payload.user);
    return payload;
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!token) {
        setUser(null);
        setIsLoading(false);
        return;
      }

      try {
        const nextUser = await loadUser(token);
        if (!cancelled) setUser(nextUser);
      } catch (e) {
        if (!cancelled) logout();
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    setIsLoading(true);
    run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadUser, token]);

  const setSession = (payload) => {
    if (payload.token) {
        localStorage.setItem(TOKEN_STORAGE_KEY, payload.token);
        setToken(payload.token);
    }
    if (payload.user) {
        setUser(payload.user);
    }
    // If payload has member, adapt it to user
    if (payload.member) {
      setUser({
        uuid: payload.member.uuid,
        email: payload.member.email || null,
        email_verified_at: payload.member.email_verified_at || null,
        name: payload.member.name || null,
        role: payload.member.role || null,
        is_guest: !payload.member.email_verified_at
      });
    }
  };

  const value = useMemo(
    () => ({ token, user, isLoading, login, register, logout, setSession, refreshUser }),
    [token, user, isLoading, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

